import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm, validators } from '@recargas-dominicanas/core/utils';
import { CustomerApi } from '@recargas-dominicanas/core/api';
import { provinces } from '@recargas-dominicanas/core/constants';
import { useIsAuth, useLocalStorage, useRemoteConfig } from '@recargas-dominicanas/core/hooks';
import { remoteConfig as config } from '../../firebase';
import { style } from './signup.module.css';

import { 
  Icon, 
  Autocomplete,
  OutlineButton,
  TextField, 
  Select,
  SelectOption,
  Title, 
  Error, 
  Brand, 
  FieldError
} from '@recargas-dominicanas/core/components';

export function Signup() {

  const navigate = useNavigate();
  const remoteConfig = useRemoteConfig(config);
  const localStorage = useLocalStorage();
  const formRef = useRef<HTMLFormElement>(null);
  const [isAuth] = useIsAuth('businessUser');
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showPassword, setShowPassword] = useState(false);
  const [isValid, setIsValid] = useState<boolean>();
  const [provinceSearch, setProvinceSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  
  const documentTypes = [
    { title: 'Cédula', value: 'id' },
    { title: 'Pasaporte', value: 'passport' }
  ];

  const form = useForm({
    name: '',
    phone: '',
    documentType: documentTypes[0] as { title: string; value: 'id' | 'passport' },
    documentNumber: '',
    email: '',
    userName: '',
    password: '',
    businessName: '',
    businessPhone: '',
    businessProvince: '',
    businessCity: '',
    businessAddress: '',
    acceptedTermsAndPrivacy: false,
  });
  
  const selectedProvince = provinces.find(province => province.name === form.fields.businessProvince.value);
  const municipalities = selectedProvince?.municipalities ?? [];

  useEffect(() => {
    form.fields.acceptedTermsAndPrivacy.addValidators([{
      message: 'Este campo es requerido',
      validate: (value) => typeof value === 'boolean' && value === true
    }]);
  }, []);

  useEffect(() => {
    if (isValid === false) {
      const element = formRef.current?.querySelector('.field-error');
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isValid]);

  async function handleLogin() {

    setErrorMessage(undefined);
    setIsValid(true);

    const isFormValid = form.isValid();
    const isUserNameValid = await validateUserName();
    const isDocumentNumberValid = await validateDocumentNumber();

    if (!isFormValid || !isUserNameValid || !isDocumentNumberValid) {
      setIsValid(false);
      return;
    }

    try {

      await CustomerApi.signup({ 
        ...form.value,
        documentType: form.value.documentType.value,
        system: 'webapp' 
      });

      localStorage.setSignupWithinServiceHours(remoteConfig.isWithinServiceHours);
      
      navigate('/');

    } catch (error: any) {

      if (error.response.status === 409) {
        setErrorMessage('Ya existe un usuario con esta correo electronico');
      } else {
        setErrorMessage('Ocurrio un error al crear la cuenta');
      }
    }
  }

  async function validateUserName() {
    
    if (!form.fields.userName.validate()) return false;

    try {
        
      const result = await CustomerApi.usernameExists(form.fields.userName.value);

      if (result.exists) {
        form.fields.userName.setError('Nombre de usuario no disponible');
        return false;
      }
    
    } catch {
    
      form.fields.userName.setError('Error validando nombre de usuario');
      return false;
    }

    return true;
  }

  async function validateDocumentNumber() {

    if (!form.fields.documentNumber.validate()) return false;

    try {

      const result = await CustomerApi.documentNumberExists(
        form.fields.documentType.value.value,
        form.fields.documentNumber.value
      );

      if (result.exists) {
        form.fields.documentNumber.setError('Este documento ya está registrado');
        return false;
      }

    } catch {

      form.fields.documentNumber.setError('Error validando número de documento');
      return false;
    }

    return true;
  }

  if (isAuth) {
    return <Navigate to='/'/>;
  }

  return (
    <div className={style.container}>
      <Brand style={style.brand}/>
      <div className={style.card}>
        <Icon className={style.cardIcon} icon='account_circle'/>
        <Title style={style.cardTitle} title='Crear cuenta'/>
        <form ref={formRef} className={style.form}>
          <div className={style.sectionTitle}>Datos del usuario</div>
          <TextField
            style={style.field} 
            label='Nombre completo'
            formField={form.fields.name}
            validators={[validators.required]}/>
          <TextField
            style={style.field} 
            label='Teléfono'
            formField={form.fields.phone}
            validators={[validators.required, validators.phone]}/>
          <Select
            style={style.field}
            label='Tipo de documento'
            formField={form.fields.documentType}
            required>
            {documentTypes.map(documentType =>
              <SelectOption 
                key={documentType.value} 
                value={documentType} 
                label={documentType.title}
                showClearIcon={false}>
                {documentType.title}
              </SelectOption>
            )}
          </Select>
          <TextField
            style={style.field} 
            label={form.value.documentType.title}
            formField={form.fields.documentNumber}
            validators={[
              validators.required,
              form.value.documentType.value === 'id'
                ? validators.cedula
                : validators.passport
            ]}
            onBlur={validateDocumentNumber}/>
          <TextField
            style={style.emailField} 
            label='Correo electrónico (opcional)'
            formField={form.fields.email}
            validators={[validators.email]}/>
          <TextField
            style={style.field}
            label='Usuario'
            formField={form.fields.userName}
            onBlur={validateUserName}
            validators={[
              validators.required,
              validators.length(4, 'El nombre de usuario debe tener mínimo 4 caracteres'),
              validators.maxLength(30, 'El nombre de usuario debe tener máximo 30 caracteres'),
              validators.username
            ]}/>
          <TextField
            style={style.field}
            type={showPassword ? 'text' : 'password'}
            label='Contraseña'
            formField={form.fields.password}
            rightIcon={
              <Icon 
                icon={showPassword ? 'visibility' : 'visibility_off'} 
                onClick={() => setShowPassword(show => !show)}/>
            }
            validators={[
              validators.required,
              validators.length(5, 'La contraseña debe tener al menos 5 caracteres')
            ]}/>
          <div className={style.sectionTitle}>Datos del negocio</div>
          <TextField
            style={style.field} 
            label='Nombre'
            formField={form.fields.businessName}
            validators={[validators.required]}/>
          <TextField
            style={style.field} 
            label='Teléfono (opcional)'
            formField={form.fields.businessPhone}
            validators={[validators.phone]}/>
          <Autocomplete
            style={style.autocomplete}
            hideIcon={true}
            label='Provincia'
            formField={form.fields.businessProvince}
            onChange={() => {
              form.fields.businessCity.clear();
              setCitySearch('');
            }}
            onSearch={value => setProvinceSearch(value ?? '')}
            required>
            {provinces
              .filter(province => province.name.toLowerCase().includes(provinceSearch.toLowerCase()))
              .map(province =>
                <SelectOption 
                  key={province.name} 
                  value={province.name} 
                  label={province.name}>
                  {province.name}
                </SelectOption>
              )}
          </Autocomplete>
          <Autocomplete
            style={style.autocomplete}
            hideIcon={true}
            label='Ciudad'
            formField={form.fields.businessCity}
            disabled={!selectedProvince}
            onSearch={value => setCitySearch(value ?? '')}
            required>
            {municipalities
              .filter(municipality => municipality.toLowerCase().includes(citySearch.toLowerCase()))
              .map(municipality =>
                <SelectOption 
                  key={municipality} 
                  value={municipality} 
                  label={municipality}>
                  {municipality}
                </SelectOption>
              )}
          </Autocomplete>
          <TextField
            style={style.addressField} 
            label='Dirección'
            formField={form.fields.businessAddress}
            validators={[validators.required]}/>
          <div className={style.termsAndPrivacyField}>
            <label className={style.termsAndPrivacyLabel}>
              <input
                className={style.termsAndPrivacyCheckbox}
                type='checkbox' 
                checked={form.fields.acceptedTermsAndPrivacy.value}
                onChange={e => form.fields.acceptedTermsAndPrivacy.update(e.target.checked)}/>
              <span>
                Acepto los <a href='https://www.recargasdominicanas.do/terminos-y-condiciones'>Términos y Condiciones</a> y la <a href='https://www.recargasdominicanas.do/politica-de-privacidad'>Política de Privacidad</a>
              </span>
            </label>
            <FieldError message={form.fields.acceptedTermsAndPrivacy.error}/>
          </div>
        </form>
        {isValid === false &&
          <FieldError
            className={style.invalidFields}
            message='*Algunos campos son inválidos'/>
        }
        <Error 
          show={errorMessage !== undefined}
          message={errorMessage!}/>
        <OutlineButton
          style={style.button}
          text='Crear cuenta'
          onClick={handleLogin}/>
      </div>
      <div className={style.loginContainer}>
        <span className={style.loginLabel}>¿Ya tienes una cuenta?</span>
        <Link to='/iniciar-sesion' className={style.loginLink}>Inicia sesión aquí</Link>
      </div>
    </div>
  );
}
